/**
 * Shared Authentication Types
 *
 * These types are used by both UI and SI applications for OAuth authentication.
 */

import type { RemoteLoggingPolicy } from '../logging/types';

/**
 * OAuth token data stored in memory (NOT persisted to localStorage)
 * Note: refreshToken is NOT stored here - it's in an HttpOnly cookie
 */
export interface OAuthTokenData {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: string;
}

/**
 * Laravel Passport OAuth token response
 */
export interface PassportTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

/**
 * Secure auth API response (from /auth/ui/refresh and /auth/si/refresh)
 * Note: On web, refresh_token is NOT in the response - it's set as HttpOnly cookie
 * On native apps (Android/iOS), refresh_token is included in the response body
 */
export interface SecureAuthResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    token_type: string;
    expires_in: number;
    /** Refresh token - only included for native app requests (X-EMALINO-Native-App: true header) */
    refresh_token?: string;
    /** Refresh token expiration - only included for native app requests */
    refresh_token_expires_at?: string;
    /** True on the first login after email/password registration; frontend uses this to clear stale onboarding acks */
    is_new_user?: boolean;
    /** Effective remote-logging policy — the client log forwarder configures itself from this block */
    remote_logging?: RemoteLoggingPolicy | null;
  };
}

/**
 * Two-factor authentication challenge response
 * Returned instead of tokens when a user has 2FA enabled
 */
export interface TwoFactorChallengeResponse {
  requires_2fa: true;
  challenge_token: string;
  method: 'totp' | 'email' | 'sms';
  expires_in: number;
}

/**
 * Login API response — either a normal token response or a 2FA challenge.
 * Used as the generic parameter for the login POST call so both shapes
 * are visible to TypeScript.
 */
export type LoginApiResponse =
  | SecureAuthResponse
  | { success: boolean; message: string; data: TwoFactorChallengeResponse };

/**
 * OAuth error response
 */
export interface OAuthErrorResponse {
  error: string;
  error_description?: string;
  error_type?: string;
  message?: string;
  hint?: string;
}

/**
 * OAuth error types
 */
export type OAuthErrorType =
  | 'invalid_credentials'
  | 'email_not_verified'
  | 'expired_token'
  | 'invalid_request'
  | 'server_error'
  | 'csrf_mismatch'
  | '2fa_required'
  | 'unknown';

/**
 * Cross-tab synchronization message types
 */
export type AuthSyncMessageType =
  | 'refresh-start'
  | 'refresh-success'
  | 'refresh-failed'
  | 'logout';

/**
 * Cross-tab synchronization message
 * Used with BroadcastChannel API to coordinate auth state across browser tabs
 */
export interface AuthSyncMessage {
  type: AuthSyncMessageType;
  timestamp: number;
  tokenData?: OAuthTokenData;
}

/**
 * Cross-tab sync configuration
 */
export interface CrossTabSyncConfig {
  /** Unique channel name per app (e.g., 'emalino-ui-auth', 'emalino-si-auth') */
  channelName: string;
  /** Whether cross-tab sync is enabled (default: true for web, false for native) */
  enabled?: boolean;
}

/**
 * Maintenance mode state
 * Triggered when API returns 503 Service Unavailable
 */
export interface MaintenanceModeState {
  /** Whether maintenance mode is currently active */
  isActive: boolean;
  /** Message from server (e.g., "System maintenance in progress") */
  message: string | null;
  /** Suggested retry time in seconds from Retry-After header */
  retryAfter: number | null;
}

/**
 * Callback invoked when maintenance mode state changes
 */
export type MaintenanceModeCallback = (
  isActive: boolean,
  message?: string,
  retryAfter?: number
) => void;

/**
 * Callback invoked on logout (for app-specific cleanup)
 */
export type LogoutCallback = () => void | Promise<void>;

/**
 * OAuth store state
 */
export interface OAuthState {
  /** Access token - memory only for XSS protection */
  tokenData: OAuthTokenData | null;
  /** Last error information */
  lastError: {
    type: OAuthErrorType | null;
    message: string | null;
  } | null;
  /** Flag to track deliberate logout vs session expiry */
  deliberateLogout: boolean;
  /** Flag to track if session restoration is in progress */
  isRestoringSession: boolean;
  /** Flag to track if user just logged in (for post-login flows) */
  justLoggedIn: boolean;
  /** True when the last successful email/password login was a new user's first login */
  isNewUser: boolean;
  /** Maintenance mode state */
  maintenanceMode: MaintenanceModeState;
}

/**
 * OAuth configuration
 */
export interface OAuthConfig {
  apiUrl: string;
  clientId: string;
  /**
   * @deprecated The OAuth client_secret is no longer sent from the browser/native
   * bundle — the API injects the confidential secret server-side by app context
   * (V1AuthController::multiAppLogin). Leave unset; kept optional only so any
   * lingering caller still type-checks. Do not reintroduce it into a build.
   */
  clientSecret?: string;
  timeout?: number;
}

/**
 * Platform detection interface
 */
export interface PlatformUtils {
  isNativeApp: () => boolean;
  isAndroid?: () => boolean;
  isIOS?: () => boolean;
}

/**
 * Secure storage interface for native apps
 */
export interface SecureStorageAdapter {
  getRefreshToken: () => Promise<string | null>;
  setRefreshToken: (token: string, expiresAt?: string) => Promise<void>;
  getCsrfToken: () => Promise<string | null>;
  setCsrfToken: (token: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

/**
 * Type guard for OAuth error response
 */
export function isOAuthError(data: unknown): data is OAuthErrorResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    'error' in data &&
    typeof (data as Record<string, unknown>).error === 'string'
  );
}

/**
 * Convert secure auth response to token data (memory-only storage)
 */
export function convertSecureAuthResponse(
  response: SecureAuthResponse['data']
): OAuthTokenData {
  const expiresAt = new Date(
    Date.now() + response.expires_in * 1000
  ).toISOString();
  return {
    accessToken: response.access_token,
    tokenType: response.token_type,
    expiresIn: response.expires_in,
    expiresAt,
  };
}

/**
 * Detect and categorize OAuth errors
 */
export function detectOAuthErrorType(
  data: unknown
): { errorType: OAuthErrorType; message: string } | null {
  if (!isOAuthError(data)) return null;

  const errorCode = data.error;
  const description =
    data.error_description || data.message || data.hint || '';

  const errorMap: Record<string, { type: OAuthErrorType; message: string }> = {
    invalid_grant: {
      type: description.toLowerCase().includes('credentials')
        ? 'invalid_credentials'
        : description.toLowerCase().includes('expired')
          ? 'expired_token'
          : 'invalid_credentials',
      message: description.toLowerCase().includes('credentials')
        ? 'Invalid email or password'
        : description.toLowerCase().includes('expired')
          ? 'Your session has expired. Please log in again'
          : description || 'Authentication failed',
    },
    invalid_request: {
      type: 'invalid_request',
      message: description || 'Invalid request format',
    },
    invalid_client: {
      type: 'server_error',
      message: 'Authentication service error',
    },
    server_error: {
      type: 'server_error',
      message: 'Server error. Please try again later',
    },
    csrf_mismatch: {
      type: 'csrf_mismatch',
      message:
        'Security validation failed. Please refresh the page and try again.',
    },
  };

  const error = errorMap[errorCode] || {
    type: 'unknown' as OAuthErrorType,
    message: description || 'Authentication failed',
  };

  return {
    errorType: error.type,
    message: error.message,
  };
}

/**
 * Get CSRF token from cookie (supports multi-app cookie isolation)
 *
 * @param appContext - The app context ('ui' or 'si') for multi-app cookie isolation
 * @returns The CSRF token value or null if not found
 */
export function getCsrfToken(appContext: 'ui' | 'si' = 'ui'): string | null {
  if (typeof document === 'undefined') return null;

  // Build app-specific cookie name
  // UI: emalino_csrf_token
  // SI: emalino_si_csrf_token
  const cookieName = appContext === 'ui' ? 'emalino_csrf_token' : 'emalino_si_csrf_token';

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const parts = cookie.trim().split('=');
    const name = parts[0];
    const value = parts.slice(1).join('=');
    if (name === cookieName && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Check whether the JS-readable session-marker cookie is present.
 *
 * The API sets this alongside the HttpOnly refresh token (and clears it on
 * logout / refresh failure). Its absence means there's no session to restore,
 * so callers can short-circuit before hitting `/auth/{ui,si}/refresh` and
 * generating a 401 + console warning for anonymous visitors. Cookie name must
 * stay in sync with `api/config/auth.php → cookies.{ui,si}.session_marker`.
 */
export function hasSessionMarkerCookie(appContext: 'ui' | 'si' = 'ui'): boolean {
  if (typeof document === 'undefined') return false;
  const cookieName = appContext === 'ui' ? 'emalino_has_session' : 'emalino_si_has_session';
  return document.cookie.split('; ').some((c) => c.startsWith(`${cookieName}=`));
}
